import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 *
 * @extends {Component<Refs>}
 */
class QuantitySelectorComponent extends Component {
  /** @type {HTMLSelectElement | null} */
  #select = null;

  connectedCallback() {
    super.connectedCallback();

    // Support dropdown-based quantity selector
    this.#select = this.querySelector('select');

    if (this.#select) {
      // Ensure the select contributes to form submission
      if (!this.#select.name && this.closest('form')) {
        this.#select.name = 'quantity';
      }

      // Sync quantity on change
      this.#select.addEventListener('change', () => this.#onQuantityChange());
    }

    // Initialize button states
    const initialQuantity = parseInt(this.refs.quantityInput?.value) || 0;
    this.#updateButtonStates(initialQuantity);
  }
  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    this.refs.quantityInput.stepUp();
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    const currentValue = parseInt(this.refs.quantityInput.value) || 0;
    
    // Don't allow decreasing below 0
    if (currentValue > 0) {
      this.refs.quantityInput.stepDown();
      this.#onQuantityChange();
    }
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    event.preventDefault();
    if (event.target instanceof HTMLInputElement) {
      this.refs.quantityInput.value = event.target.value;
    }
    this.#onQuantityChange();
  }

  /**
   * Handles the quantity change event.
   */
  #onQuantityChange() {
    const { quantityInput } = this.refs;

    this.#checkQuantityRules();

    const rawValue = this.#select ? this.#select.value : quantityInput?.value;
    const newValue = parseInt(rawValue);

    // Update button states based on quantity
    this.#updateButtonStates(newValue);

    const dispatchTarget = this.#select ?? quantityInput;
    if (!dispatchTarget) return;

    dispatchTarget.dispatchEvent(
      new QuantitySelectorUpdateEvent(newValue, Number(quantityInput?.dataset.cartLine))
    );
  }

  /**
   * Updates button states based on current quantity
   * @param {number} quantity - The current quantity value
   */
  #updateButtonStates(quantity) {
    const minusButton = this.querySelector('.quantity-minus');
    const addButton = this.closest('.quick-order-list__row')?.querySelector('.quick-order-list__add');
    
    // Disable minus button when quantity is 0
    if (minusButton) {
      minusButton.disabled = quantity <= 0;
    }
    
    // Disable add button when quantity is 0
    if (addButton) {
      addButton.disabled = quantity <= 0;
    }
    
    // Remove any background styling when quantity is 0
    if (quantity === 0) {
      this.style.backgroundColor = '';
      this.style.opacity = '';
      this.style.boxShadow = '';
    }
  }

  /**
   * Checks the quantity rules are met
   */
  #checkQuantityRules = () => {
    // Only applies to numeric input variant; select options are predefined
    if (this.#select) return;

    const { quantityInput } = this.refs;
    if (!quantityInput) return;

    const { max, value: newValue } = quantityInput;
    const numericValue = parseInt(newValue);

    // Allow zero values, but enforce minimum of 0
    if (numericValue < 0) quantityInput.value = 0;
    if (numericValue > max && max) quantityInput.value = max;
  };

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
